// Rekenwerk voor Planning · Tijdlijn (design/build/proto-tijdlijn-logic.js): het venster, posities in procenten van
// de tijdlijn, het label naast een balk, en afhankelijkheden (einde-naar-start) die geschonden zijn en hoe ver een
// opvolger dan moet opschuiven.
import { addDays, ddmm, diffDays, dmy, isoWeek, mondayOf } from "./planning";
import type { Dep } from "./types";

export const MON = ["jan", "feb", "mrt", "apr", "mei", "jun", "jul", "aug", "sep", "okt", "nov", "dec"];
export const MONL = ["januari", "februari", "maart", "april", "mei", "juni", "juli", "augustus", "september", "oktober", "november", "december"];

export type WinKind = "m4" | "w8";
export interface Win { from: string; to: string; days: number }

/** eerste dag van de maand, n maanden na die van s */
const monthStart = (s: string, n: number) => {
  const m = Number(s.slice(5, 7)) - 1 + n, y = Number(s.slice(0, 4)) + Math.floor(m / 12);
  return `${y}-${String((m % 12 + 12) % 12 + 1).padStart(2, "0")}-01`;
};

/** 4 maanden: deze maand en de drie erna. 8 weken: vanaf de maandag van deze week. */
export function windowOf(kind: WinKind, today: string): Win {
  const from = kind === "w8" ? mondayOf(today) : today.slice(0, 7) + "-01";
  const to = kind === "w8" ? addDays(from, 55) : addDays(monthStart(from, 4), -1);
  return { from, to, days: diffDays(from, to) + 1 };
}

export const clamp = (v: number) => Math.max(0, Math.min(100, v));
export const pct = (v: number) => Math.round(v * 100) / 100 + "%";

/** links en rechts van een periode (start tot en met de einddag), in % van het venster */
export function spanOf(W: Win, start: string, end: string) {
  return { l: clamp((diffDays(W.from, start) / W.days) * 100), r: clamp(((diffDays(W.from, end) + 1) / W.days) * 100) };
}
/** het midden van een dag, in % van het venster */
export const dayMid = (W: Win, day: string) => ((diffDays(W.from, day) + 0.5) / W.days) * 100;

/** Label naast een balk: rechts ervan, links ervan als de balk tegen de rechterrand zit, of op het einde van een lange balk. */
export function tagAt(l: number, r: number) {
  const mode = r > 76 ? (l < 30 ? "in" : "left") : "right";
  return {
    cls: "p-tltag" + (mode === "in" ? " in" : ""),
    left: mode === "right" ? pct(r + 0.6) : "auto",
    right: mode === "left" ? pct(100 - l + 0.6) : mode === "in" ? pct(100 - r + 0.4) : "auto",
  };
}

/** kop: maanden en weeknummers */
export function monthMarks(W: Win) {
  const out: { key: string; label: string; l: string; w: string }[] = [];
  for (let m = W.from.slice(0, 7) + "-01"; m <= W.to; ) {
    const next = monthStart(m, 1), l = clamp((diffDays(W.from, m) / W.days) * 100), r = clamp((diffDays(W.from, next) / W.days) * 100);
    out.push({ key: m, label: MON[Number(m.slice(5, 7)) - 1], l: pct(l), w: pct(r - l) });
    m = next;
  }
  return out;
}
export function weekMarks(W: Win) {
  const out: { key: string; label: string; l: string }[] = [];
  for (let m = mondayOf(W.from); m <= W.to; m = addDays(m, 7)) if (m >= W.from) out.push({ key: m, label: String(isoWeek(m)), l: pct((diffDays(W.from, m) / W.days) * 100) });
  return out;
}

/** datum zonder jaar, met jaar als het een ander jaar is dan vandaag */
export const dd = (s: string, today: string) => (s.slice(0, 4) === today.slice(0, 4) ? ddmm(s) : dmy(s));
export const nOf = (x: number, one: string, many: string) => x + " " + (x === 1 ? one : many);
export const andList = (a: string[]) => (a.length < 2 ? a.join("") : a.slice(0, -1).join(", ") + " en " + a[a.length - 1]);

export interface Dated { start: string; end: string }
export interface Violation { dep: Dep; pe: string; ss: string }
/** null: afgewerkt of onbekend, dus geen voorganger meer om op te wachten */
export type DateOf = (id: string) => Dated | null;

/** Opvolgers die beginnen vóór hun voorganger klaar is (einde-naar-start, type 1). `isSucc`: een open taak die meetelt. */
export function findViolations(deps: Dep[], dateOf: DateOf, isSucc: (id: string) => boolean): Violation[] {
  const out: Violation[] = [];
  for (const d of deps) {
    if (d.type !== 1 || !isSucc(d.succ)) continue;
    const p = dateOf(d.pred), s = dateOf(d.succ);
    if (p && s && s.start <= p.end) out.push({ dep: d, pe: p.end, ss: s.start });
  }
  return out;
}

/**
 * Hoeveel dagen elke opvolger later moet om de dag na zijn voorganger te starten (start en einde samen). Wat daardoor
 * op zijn beurt te vroeg begint, schuift mee. Een kring (kan in Planner niet, maar toch) loopt niet rond.
 */
export function fixShifts(list: Violation[], deps: Dep[], dateOf: DateOf, isSucc: (id: string) => boolean): Map<string, number> {
  const shift = new Map<string, number>();
  const at = (id: string): Dated | null => {
    const d = dateOf(id), n = shift.get(id) || 0;
    return d && n ? { start: addDays(d.start, n), end: addDays(d.end, n) } : d;
  };
  let todo = list.map((v) => ({ d: v.dep, path: new Set([v.dep.pred]) }));
  for (let round = 0; round < 50 && todo.length; round++) {
    const next: typeof todo = [];
    for (const { d, path } of todo) {
      const p = at(d.pred), s = at(d.succ);
      if (path.has(d.succ) || !p || !s || s.start > p.end) continue;
      shift.set(d.succ, (shift.get(d.succ) || 0) + diffDays(s.start, p.end) + 1);
      const via = new Set(path).add(d.succ);
      for (const x of deps) if (x.pred === d.succ && x.type === 1 && isSucc(x.succ)) next.push({ d: x, path: via });
    }
    todo = next;
  }
  return shift;
}
